import CustomDatePicker from '@/components/shared/CustomDatePicker';
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, AlertCircle } from 'lucide-react';
import PicSelector from '@/components/shared/PicSelector';
import ProductItemForm, { makeComp } from './ProductItemForm';
import { generateNextPoNumber } from '@/lib/poNumberGenerator';

const CORE_CATEGORIES = ['botol', 'bibit', 'tutup', 'spray', 'bahan_cair', 'tambahan'];

const makeProduct = (idx) => ({
  _id: Date.now() + idx,
  product_name: '',
  target_qty: '',
  ukuran_botol_ml: '',
  is_dynamic_qty: false,
  racikanList: [],
  coreComponents: CORE_CATEGORIES.map(cat => ['botol', 'tutup', 'spray', 'bibit'].includes(cat) ? [makeComp(cat)] : []),
});

export default function AddOrderDialog({ onSave, trigger, initialData }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialData?.form || {
    order_number: '',
    brand_name: '',
    deadline: '',
    notes: '',
    pic_marketing: '',
  });
  const [products, setProducts] = useState(initialData?.products || [makeProduct(0)]);

  // Fetch all existing POs for auto sequence number generation
  const { data: existingPOs = [] } = useQuery({
    queryKey: ['production-orders-all'],
    queryFn: () => base44.entities.ProductionOrder.list('-created_at'),
  });

  // Preview auto PO number when modal opens
  React.useEffect(() => {
    if (open) {
      if (initialData) {
        setForm(initialData.form || { order_number: '', brand_name: '', deadline: '', notes: '', pic_marketing: '', customerAddress: '', phone: '', customerName: '' });
        if (initialData.products?.length > 0) {
          setProducts(initialData.products);
        }
      } else {
        const previewNo = generateNextPoNumber(existingPOs);
        setForm(f => ({ ...f, order_number: previewNo }));
      }
    }
  }, [open, initialData, existingPOs]);

  // Fetch semua inventory sources
  const { data: rawInventory = [] } = useQuery({ queryKey: ['inventory'], queryFn: () => base44.entities.InventoryItem.list('item_name') });
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: bahanList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });

  const inventoryItems = useMemo(() => {
    const fromStok = [
      ...botolList.map(i => ({ id: `botol-${i.id}`, item_name: i.nama, category: 'botol', total_stock: i.stok ?? 0, unit: 'pcs', vendor: i.catatan_teknis || '', minimum_stock: 10, dari_client: i.dari_client, client_owner: i.client_owner })),
      ...tutupList.map(i => ({ id: `tutup-${i.id}`, item_name: i.nama, category: 'tutup', total_stock: i.stok ?? 0, unit: 'pcs', vendor: i.catatan || '', minimum_stock: 10, dari_client: i.dari_client, client_owner: i.client_owner })),
      ...sprayList.map(i => ({ id: `spray-${i.id}`, item_name: i.nama, category: 'spray', total_stock: i.stok ?? 0, unit: 'pcs', vendor: i.tipe || '', minimum_stock: 10, dari_client: i.dari_client, client_owner: i.client_owner })),
      ...bahanList.map(i => ({ id: `bahan-${i.id}`, item_name: i.nama, category: i.kategori === 'Bibit' ? 'bibit' : 'bahan', total_stock: i.stok ?? 0, unit: i.satuan || 'kg', vendor: i.vendor || '', minimum_stock: 0.5, dari_client: i.dari_client, client_owner: i.client_owner })),
      ...fullsetList.map(i => ({ id: `fullset-${i.id}`, item_name: i.nama, category: 'botol', total_stock: i.stok ?? 0, unit: 'pcs', vendor: i.catatan || '', minimum_stock: 10, is_fullset: true, dari_client: i.dari_client, client_owner: i.client_owner })),
    ];
    const stokNames = new Set(fromStok.map(i => String(i.item_name || '').toLowerCase()));
    const onlyRaw = rawInventory.filter(i => !stokNames.has(String(i.item_name || '').toLowerCase()));
    return [...fromStok, ...onlyRaw];
  }, [botolList, tutupList, sprayList, bahanList, fullsetList, rawInventory]);

  // Cari item inventory berdasarkan kategori + nama + vendor
  const findInv = (category, nama, vendor) => {
    if (!nama) return null;
    const nameLower = String(nama || '').toLowerCase().trim();
    const vendorLower = String(vendor || '').toLowerCase().trim();
    const items = inventoryItems.filter(i => i.category === category);

    // 1. Exact match (nama + vendor)
    if (vendorLower) {
      const exactWithVendor = items.find(i => 
        String(i.item_name || '').toLowerCase().trim() === nameLower &&
        String(i.vendor || '').toLowerCase().trim() === vendorLower
      );
      if (exactWithVendor) return exactWithVendor;
    }

    // 2. Exact match nama
    const exact = items.find(i => String(i.item_name || '').toLowerCase().trim() === nameLower);
    if (exact) return exact;

    // 3. Semua kata cocok (dengan vendor jika ada)
    const words = nameLower.split(/\s+/).filter(w => w.length > 1);
    if (vendorLower) {
      const allMatchVendor = items.find(i => {
        const invName = String(i.item_name || '').toLowerCase();
        const invVendor = String(i.vendor || '').toLowerCase();
        return words.every(w => invName.includes(w)) && invVendor.includes(vendorLower);
      });
      if (allMatchVendor) return allMatchVendor;
    }

    const allMatch = items.find(i => {
      const invName = String(i.item_name || '').toLowerCase();
      return words.every(w => invName.includes(w));
    });
    if (allMatch) return allMatch;

    return null;
  };

  const updateProduct = (idx, updated) => {
    setProducts(prev => prev.map((p, i) => i === idx ? updated : p));
  };

  const removeProduct = (idx) => {
    setProducts(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  };

  // Kalkulasi penggunaan stok global untuk variant-variant sebelumnya
  const allPriorUsages = useMemo(() => {
    const usages = [];
    const runningUsage = {};
    products.forEach((p) => {
      usages.push({ ...runningUsage });
      p.coreComponents.flat().forEach(comp => {
        if (comp.inventory_item_id && comp.qty_needed > 0) {
          runningUsage[comp.inventory_item_id] = (runningUsage[comp.inventory_item_id] || 0) + comp.qty_needed;
        }
      });
    });
    return usages;
  }, [products]);

  const addProduct = () => {
    setProducts(prev => [...prev, makeProduct(prev.length)]);
  };

  const resetForm = () => {
    setForm(initialData?.form || { order_number: '', brand_name: '', deadline: '', notes: '', pic_marketing: '' });
    setProducts(initialData?.products || [makeProduct(0)]);
  };

  const totalNeedBuy = products.reduce((sum, p) =>
    sum + p.coreComponents.flat().filter(c => c.status === 'need_buy').length, 0
  );
  const totalLinked = products.reduce((sum, p) =>
    sum + p.coreComponents.flat().filter(c => c.inventory_item_id).length, 0
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.brand_name) return;

    // Tentukan Nomor PO secara presisi tepat saat PO benar-benar disimpan
    let latestPOs = [];
    try {
      latestPOs = await base44.entities.ProductionOrder.list('-created_at');
    } catch (e) {
      latestPOs = existingPOs;
    }
    const finalPoNumber = form.order_number?.trim() || generateNextPoNumber(latestPOs);

    const finalProducts = products.map((p) => {
      const qty = Number(p.target_qty) || 0;
      const ukuranMl = Number(p.ukuran_botol_ml) || 0;

      const allComponents = [];
      CORE_CATEGORIES.forEach((cat, catIdx) => {
        p.coreComponents[catIdx].forEach(comp => {
          const isFromClient = ['pending_client', 'ready_client'].includes(comp.status);
          if (!comp.inventory_item_id && !isFromClient) return;
          allComponents.push({
            name: comp.item_name || cat,
            category: cat,
            inventory_item_id: comp.inventory_item_id,
            item_name: comp.item_name,
            vendor: comp.vendor,
            qty_needed: comp.qty_needed || 0,
            stock_available: comp.stock_available || 0,
            unit: comp.unit || 'pcs',
            status: comp.status || 'pending',
            item_notes: comp.item_notes || '',
            is_integrated: true,
            color_code: '',
            target_qty: comp.qty_needed || 0,
            current_qty: 0,
          });
        });
      });

      const racikanWithCalc = p.racikanList.map(r => {
        const ml = ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * ukuranMl * qty;
        const kebutuhan = ml >= 1000
          ? { nilai: +(ml / 1000).toFixed(2), satuan: 'L' }
          : { nilai: +ml.toFixed(2), satuan: 'ml' };
        return {
          nama_bahan: r.nama_bahan,
          kategori_bahan: r.kategori_bahan,
          persentase: r.persentase,
          vendor: r.vendor || '',
          kebutuhan_nilai: ukuranMl > 0 && qty > 0 ? kebutuhan.nilai : undefined,
          kebutuhan_satuan: ukuranMl > 0 && qty > 0 ? kebutuhan.satuan : undefined,
        };
      });

      return {
        _id: p._id || Date.now() + Math.random(),
        product_name: p.product_name,
        target_qty: qty,
        ukuran_botol_ml: ukuranMl || undefined,
        is_dynamic_qty: p.is_dynamic_qty,
        racikan_digunakan: racikanWithCalc,
        components: allComponents,
        status: totalNeedBuy > 0 ? 'sedang_po_item' : 'antrian_produksi'
      };
    });

    // Check if an existing PO already has this order_number to prevent accidental duplicate inserts
    const existingWithSameNum = (latestPOs || []).find(p => 
      p.id === initialData?.id || 
      (p.order_number && String(p.order_number).trim().toLowerCase() === finalPoNumber.toLowerCase())
    );

    const newPO = {
      ...(initialData?.id || existingWithSameNum?.id ? { id: initialData?.id || existingWithSameNum?.id } : {}),
      order_number: finalPoNumber,
      brand_name: form.brand_name,
      deadline: form.deadline,
      notes: form.notes,
      pic_marketing: form.pic_marketing,
      customerAddress: form.customerAddress || initialData?.form?.customerAddress || initialData?.customerAddress || '',
      phone: form.phone || initialData?.form?.phone || initialData?.phone || '',
      customerName: form.customerName || initialData?.form?.customerName || initialData?.customerName || '',
      status: totalNeedBuy > 0 ? 'sedang_po_item' : 'antrian_produksi',
      products: finalProducts,
      product_name: finalProducts.length === 1 ? finalProducts[0].product_name : `Multi (${finalProducts.length} Produk)`,
      target_qty: finalProducts.length === 1 ? finalProducts[0].target_qty : 0,
      components: finalProducts.flatMap(p => p.components),
      racikan_digunakan: finalProducts.flatMap(p => p.racikan_digunakan)
    };

    await onSave(newPO);

    setOpen(false);
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Tambah PO Baru</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ===== Info PO Header ===== */}
          <div className="space-y-3 p-4 bg-muted/30 rounded-xl border border-border">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Info PO</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">No. PO (Otomatis) *</Label>
                <Input
                  value={form.order_number}
                  onChange={(e) => setForm(f => ({ ...f, order_number: e.target.value }))}
                  required
                  placeholder="e.g. 001-08-26"
                  className="mt-1 h-9 font-semibold text-primary"
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">Format: XXX-MM-YY (Reset tiap bulan)</p>
              </div>
              <div>
                <Label className="text-xs">Nama Brand *</Label>
                <Input
                  value={form.brand_name}
                  onChange={(e) => setForm(f => ({ ...f, brand_name: e.target.value }))}
                  required
                  placeholder="D'BLURE, SEVNOV..."
                  className="mt-1 h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Deadline</Label>
                <CustomDatePicker
                  value={form.deadline}
                  onChange={(val) => setForm(f => ({ ...f, deadline: val }))}
                  className="mt-1 h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Catatan Umum</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Keterangan..."
                  className="mt-1 h-9"
                />
              </div>
            </div>
            <PicSelector
              value={form.pic_marketing}
              onChange={v => setForm(f => ({ ...f, pic_marketing: v }))}
              label="PIC Marketing (Penanggung Jawab Order)"
              placeholder="Pilih marketing yang handle PO ini..."
              picType="marketing"
              requiredPageAccessKey="po_produksi"
            />
          </div>

          {/* ===== Status summary ===== */}
          {totalLinked > 0 && (
            <div className={`flex items-center gap-3 p-3 rounded-xl border text-sm ${totalNeedBuy > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <AlertCircle className={`w-4 h-4 shrink-0 ${totalNeedBuy > 0 ? 'text-red-600' : 'text-green-600'}`} />
              {totalNeedBuy > 0
                ? <p className="text-red-700 font-medium">{totalNeedBuy} item perlu dibeli — stok tidak cukup</p>
                : <p className="text-green-700 font-medium">Semua {totalLinked} item siap (stok mencukupi)</p>
              }
            </div>
          )}

          {/* ===== Daftar Produk ===== */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Produk ({products.length})
              </h3>
            </div>

            {products.map((product, idx) => (
              <ProductItemForm
                key={product._id}
                index={idx}
                product={product}
                onChange={(updated) => updateProduct(idx, updated)}
                onRemove={() => removeProduct(idx)}
                inventoryItems={inventoryItems}
                findInv={findInv}
                priorUsageMap={allPriorUsages[idx]}
                brandName={form.brand_name}
              />
            ))}

            <Button type="button" variant="outline" onClick={addProduct}
              className="w-full h-9 text-sm gap-2 border-dashed">
              <Plus className="w-4 h-4" />
              Tambah Produk
            </Button>
          </div>

          <Button type="submit" className="w-full h-11 text-base font-semibold">
            Simpan PO ({products.length} Produk)
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
